// KudosPanel.jsx — give + receive kudos wall
import React, { useEffect, useState } from "react";
import DashboardPanel from "../dashboard/DashboardPanel";
import KudosBanner from "./KudosBanner";
import { toast } from "../ui/Toast";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON = "#00FF99";

const KUDOS_TYPES = [
  { key: "team_player", icon: "🤝", label: "Team Player" },
  { key: "innovator",   icon: "💡", label: "Innovator"   },
  { key: "helpful",     icon: "🙌", label: "Helpful"     },
  { key: "star",        icon: "⭐", label: "Star"        },
  { key: "leader",      icon: "🚀", label: "Leader"      },
];

export default function KudosPanel() {
  const [received, setReceived] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [giving, setGiving] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [message, setMessage] = useState("");

  const headers = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/api/kudos/received`, { headers: headers() }).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/users/directory`, { headers: headers() }).then(r => r.ok ? r.json() : []),
    ])
      .then(([kudos, users]) => {
        setReceived(Array.isArray(kudos) ? kudos : []);
        // Accept either array or {data: [...]} or {users: [...]} shape
        const list = Array.isArray(users) ? users : (users?.data || users?.users || []);
        // Just keep id + full_name for the dropdown
        const simple = list.map(u => ({
          id: u.id,
          full_name: u.full_name || u.name || u.email,
        })).filter(u => u.full_name);
        // Sort alphabetically
        simple.sort((a, b) => a.full_name.localeCompare(b.full_name));
        setColleagues(simple);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const sendKudos = async () => {
    if (!selectedUser || !selectedType) return;
    try {
      // 1. Record the kudos
      const res = await fetch(`${API_BASE}/api/kudos/give`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          recipient_id: selectedUser.id,
          type: selectedType.key,
          message: message.trim() || null,
        }),
      });

      // 2. Post a DRAMATIC DM notification to the recipient 🎉
      const senderName = (() => {
        try {
          const u = JSON.parse(localStorage.getItem("user") || "{}");
          return u.full_name || u.name || "A colleague";
        } catch {
          return "A colleague";
        }
      })();

      const dramaticText = [
        "🎉🎊✨  K U D O S  ✨🎊🎉",
        "",
        `${selectedType.icon}  ${selectedType.label.toUpperCase()}  ${selectedType.icon}`,
        "",
        `🌟 ${senderName} just recognised you! 🌟`,
        message.trim() ? `\n💬 "${message.trim()}"` : "",
        "",
        "You're making a real difference — keep shining! ⭐",
      ].filter(Boolean).join("\n");

      fetch(`${API_BASE}/api/dm/send`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          to_user_id: selectedUser.id,
          text: dramaticText,  // fallback for clients that don't render kudos cards
          message_type: "kudos",
          meta: {
            type: selectedType.key,           // team_player | innovator | etc.
            message: message.trim() || null,
            from_name: senderName,
            to_name: selectedUser.full_name,
          },
        }),
      }).catch(() => {});

      // 3. Post to the company announcements feed (so everyone sees it)
      fetch(`${API_BASE}/api/announcements/post`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          title: `${selectedType.icon} Kudos for ${selectedUser.full_name}`,
          body: `${selectedType.label} — ${message.trim() || "Great work!"}`,
          category: "kudos",
          visibility: "company",
        }),
      }).catch(() => {});

      // 4. Also post to the celebrations feed (real-time moments)
      fetch(`${API_BASE}/api/celebrations/event`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          type: "kudos",
          user_id: selectedUser.id,
          name: selectedUser.full_name,
          detail: `received ${selectedType.label} kudos`,
          meta: {
            type: selectedType.key,
            message: message.trim() || null,
            from_name: senderName,
            to_name: selectedUser.full_name,
          },
        }),
      }).catch(() => {});

      // 5. Clear form
      setGiving(false);
      setSelectedUser(null);
      setSelectedType(null);
      setMessage("");

      // Success toast
      toast.success(`🎉 Kudos sent to ${selectedUser.full_name}!`);
    } catch (e) {
      console.error("Kudos send failed:", e);
      toast.error("Could not send kudos. Please try again.");
    }
  };

  return (
    <DashboardPanel
      title="Kudos wall"
      icon="🏆"
      loading={loading}
      headerRight={
        !giving && (
          <button
            onClick={() => setGiving(true)}
            className="text-[10px] font-semibold px-2 py-1 rounded-md transition"
            style={{ background: NEON, color: "#000" }}
          >
            + Give kudos
          </button>
        )
      }
    >
      {giving ? (
        <div className="space-y-3">
          {/* Pick colleague */}
          <div>
            <p className="text-[10px] uppercase text-white/50 mb-1 tracking-widest">Recipient</p>
            <select
              value={selectedUser?.id || ""}
              onChange={(e) => {
                const u = colleagues.find((c) => String(c.id) === e.target.value);
                setSelectedUser(u);
              }}
              className="w-full bg-black/60 border border-[#00FF99]/30 rounded-lg px-2 py-1.5 text-sm text-white outline-none"
            >
              <option value="">Select colleague…</option>
              {colleagues.map((c) => (
                <option key={c.id} value={c.id} style={{ background: "#000" }}>
                  {c.full_name || c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Pick kudos type */}
          <div>
            <p className="text-[10px] uppercase text-white/50 mb-1 tracking-widest">Recognition</p>
            <div className="grid grid-cols-5 gap-1">
              {KUDOS_TYPES.map((k) => (
                <button
                  key={k.key}
                  onClick={() => setSelectedType(k)}
                  className="flex flex-col items-center gap-0.5 py-2 rounded-md border transition"
                  style={{
                    borderColor: selectedType?.key === k.key ? NEON : "rgba(255,255,255,0.1)",
                    background: selectedType?.key === k.key ? `${NEON}15` : "transparent",
                  }}
                >
                  <span className="text-base">{k.icon}</span>
                  <span className="text-[8px] text-white/60 text-center leading-tight">{k.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Optional message…"
            maxLength={140}
            rows={2}
            className="w-full bg-black/60 border border-[#00FF99]/30 rounded-lg px-2 py-1.5 text-xs text-white outline-none resize-none"
          />

          <div className="flex gap-2">
            <button
              onClick={sendKudos}
              disabled={!selectedUser || !selectedType}
              className="flex-1 py-1.5 text-xs font-semibold rounded-md transition disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: NEON, color: "#000" }}
            >
              Send kudos
            </button>
            <button
              onClick={() => { setGiving(false); setSelectedUser(null); setSelectedType(null); setMessage(""); }}
              className="py-1.5 px-3 text-xs text-white/70 rounded-md border border-white/10"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          {/* Received count */}
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-3xl font-bold" style={{ color: NEON }}>
              {received.length}
            </span>
            <span className="text-[11px] text-white/50 uppercase tracking-wide">kudos received</span>
          </div>

          {/* Recent kudos — certificate cards */}
          {received.length === 0 ? (
            <p className="text-xs text-white/40 text-center py-3">
              No kudos yet — be the first to give!
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              {received.slice(0, 2).map((k) => (
                <KudosBanner
                  key={k.id}
                  type={k.type}
                  name={k.from_name}
                  photoUrl={k.from_photo}
                  message={k.message}
                  compact
                />
              ))}
            </div>
          )}
        </div>
      )}
    </DashboardPanel>
  );
}